import { useState } from "react";
import { toast } from "react-toastify";
import "./MusicActions.css";
import UploadPreviewDialog from "./UploadPreviewDialog";

interface CSVMusicUploadFormProps {
  competitionId: number;
  event: string | {
      id :number;
      location: string;
      start_date: string;
  };
  onUploadSuccess: (newSongs: any[]) => void;
  canSeeCSVFileUpload: boolean;
  onUploadComplete: () => void;
}

const REQUIRED_COLUMNS = ["song_id", "program", "class", "style", "description"];

const CSVMusicUpload = ({ competitionId, event, onUploadSuccess, canSeeCSVFileUpload, onUploadComplete }: CSVMusicUploadFormProps) => {

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<{   add: number[]; update: number[]; delete: number[]; locked: number[] }>({ add: [], update: [], delete: [], locked: [] });
  const [pendingUploadParams, setPendingUploadParams] = useState<{ eventId: number; queryParams: URLSearchParams; token: string | null } | null>(null);

  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleCSVChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      toast.warn("File must be a CSV");
      setCsvFile(null);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text !== "string") {
        toast.warn("Invalid file content");
        setCsvFile(null);
        return;
      }

      const lines = text.split("\n");
      const header = lines[0].trim().split(",").map(h => h.trim());
      const missingColumns = REQUIRED_COLUMNS.filter(col => !header.includes(col));
      
      if (header.length !== REQUIRED_COLUMNS.length) {
        toast.warn(
          <div>
            CSV must have exactly {REQUIRED_COLUMNS.length} columns<br />
            Required columns: {REQUIRED_COLUMNS.join(", ")}<br />
            Found: {header.length} columns
          </div>
        );
        setCsvFile(null);
        return;
      }

      if (missingColumns.length > 0) {
        toast.warn(`CSV is missing columns: ${missingColumns.join(", ")}`);
        setCsvFile(null);
        return;
      }

      setCsvFile(file);
    };

    reader.readAsText(file);
  };

  function canonicalizeName(input: string): string {
  if (!input) return '';
  return String(input)
    .normalize('NFKD')                 // normalize unicode
    .replace(/[^\w\s]/g, '')           // remove ALL special chars
    .replace(/_/g, '')                 // remove underscores too (optional but safest)
    .replace(/\s+/g, '_')              // collapse spaces
    .trim();                           // trim edges
}

  const handleCSVSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!csvFile) {
        toast.warn("Please select a valid CSV file before submitting");
        return;
      }

      const eventId = typeof event === 'string' ? parseInt(event) : event?.id;
      let location = '';
      let startDate = '';
      if (typeof event !== 'string') {
        location = canonicalizeName(event?.location);
        startDate = event.start_date.replace(/\//g, '_');
      }

      const queryParams = new URLSearchParams({
        location: encodeURIComponent(location),
        startDate: encodeURIComponent(startDate)
      });

      const token = localStorage.getItem("token");
      setIsSubmitting(true);

      try {
        const previewFormData = new FormData();
        previewFormData.append("file", csvFile);

        const previewResponse = await fetch(
          `${import.meta.env.VITE_API_URL}/admin/${competitionId}/events/${eventId}/preview-event-songs-upload?${queryParams}`,
          { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: previewFormData }
        );

        const previewResult = await previewResponse.json();

        if (!previewResponse.ok) {
          toast.warn(previewResult.message || "Preview failed");
          return;
        }

        if (previewResult.data.is_fresh_upload) {
          await doActualUpload(eventId, queryParams, token);
          return;
        }
        
        const { add, update, delete: del, locked } = previewResult.data.preview;
        setPreviewData({ add, update, delete: del, locked });
        setPendingUploadParams({ eventId, queryParams, token });
        setPreviewOpen(true);

      } catch (err) {
        toast.warn(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setIsSubmitting(false);
      }
    };

    const handlePreviewConfirm = async () => {
      setPreviewOpen(false);
      if (!pendingUploadParams) return;
      setIsSubmitting(true);
      try {
        await doActualUpload(pendingUploadParams.eventId, pendingUploadParams.queryParams, pendingUploadParams.token);
      } finally {
        setIsSubmitting(false);
        setPendingUploadParams(null);
      }
    };

    const handlePreviewCancel = () => {
      setPreviewOpen(false);
      setPendingUploadParams(null);
      setIsSubmitting(false);
    };

    const doActualUpload = async (eventId: number, queryParams: URLSearchParams, token: string | null) => {
      const formData = new FormData();
      formData.append("file", csvFile!);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/${competitionId}/events/${eventId}/upload-event-songs-list?${queryParams}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData }
      );

      const result = await response.json();

      if (!response.ok) {
        toast.warn(result.message || "Upload failed");
        return;
      }

      toast.info("CSV uploaded successfully!");
      onUploadSuccess(result.data.musicRecords);
      setCsvFile(null);
      if (onUploadComplete) onUploadComplete();
      const fileInput = document.getElementById("songsCsv") as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    };

  if (!canSeeCSVFileUpload) return null;

return (
  <div className="form-container">
    <h2 style={{ fontSize: "24px" }}>Upload Music CSV</h2>
    <form onSubmit={handleCSVSubmit}>
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
      <label style={{ fontWeight: "normal", marginRight: '15px' }}> Setup CSV for Directors</label>
      <a
      href="https://docs.google.com/spreadsheets/d/1PAovRtZmi2mvV_cc2-X_3G94wp1JjyZAg8LyhlkDV9A/edit?gid=526662826#gid=526662826"
      target="_blank"
      rel="noopener noreferrer"
      > Sample Music Setup CSV </a>
      </div>
        <input type="file" id="songsCsv" accept=".csv" onChange={handleCSVChange} style={{ marginLeft: "25px" }}/>
        <button className="formsubmit__submitbtn" type="submit"  disabled={isSubmitting || !csvFile} style={{ marginLeft: "25px", width: "120px"}}>
          {isSubmitting ? "Uploading..." : "Submit"}
        </button>
    </form>

    <UploadPreviewDialog
      open={previewOpen}
      preview={previewData}
      onConfirm={handlePreviewConfirm}
      onCancel={handlePreviewCancel}
    />

</div> );
};

export default CSVMusicUpload;